"use client"

// FontAwesome imports removed - using native <i> tags for better compatibility
import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { generateWordPressMatchUrl } from '@/lib/wordpress-slug-utils'
import { getTeamLogoUrlWithFallback, getCasterAvatarUrlWithFallback } from '@/lib/cdn-utils'
import { Match } from '@/lib/database'
import { useLiveScore } from '@/hooks/useLiveScore'
import { getMatchStatusDisplay } from '@/lib/match-status-utils'

interface NewMatchCardProps {
	match: Match
	isLoaded?: boolean
	priority?: boolean // For above-the-fold cards that should load immediately
	allMatches?: Match[] // All matches để detect duplicates
	duplicateIndex?: number // Index cho duplicate matches
}

export default function NewMatchCard({ match, isLoaded = false, priority = false, allMatches = [], duplicateIndex }: NewMatchCardProps) {
	// 🚀 Intersection Observer for lazy loading
	const cardRef = useRef<HTMLAnchorElement>(null)
	const skeletonRef = useRef<HTMLDivElement>(null)
	const [isVisible, setIsVisible] = useState(false)
	const [shouldLoad, setShouldLoad] = useState(false)
	
	// 🚀 Progressive Loading States - Data-driven, no arbitrary delays
	const [imagesLoaded, setImagesLoaded] = useState(false)
	const [imageLoadCount, setImageLoadCount] = useState(0)
	const [scoresReady, setScoresReady] = useState(false)
	
	// Intersection Observer for lazy loading (skip if priority)
	useEffect(() => {
		// Priority cards load immediately (first 3-4 cards)
		if (priority) {
			setShouldLoad(true)
			return
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setIsVisible(true)
						// Load data after short delay when visible
						setTimeout(() => setShouldLoad(true), 100)
						observer.unobserve(entry.target)
					}
				})
			},
			{
				rootMargin: '200px 0px', // Load 200px before coming into view for smoother experience
				threshold: 0.1
			}
		)

		// Observe skeleton initially, then switch to card when loaded
		const elementToObserve = skeletonRef.current || cardRef.current
		if (elementToObserve) {
			observer.observe(elementToObserve)
		}

		return () => observer.disconnect()
	}, [priority])
	
	// Enable images only when card should load
	useEffect(() => {
		if (shouldLoad) {
			setImagesLoaded(true)
			
			// Preload critical images for even faster display
			if (match.team_hone_avatar) {
				const img = new Image()
				img.src = getTeamLogoUrlWithFallback(match.team_hone_avatar).src
			}
			if (match.team_away_avatar) {
				const img = new Image()
				img.src = getTeamLogoUrlWithFallback(match.team_away_avatar).src
			}
		}
	}, [shouldLoad, match.team_hone_avatar, match.team_away_avatar])
	
	// Track image loading completion
	const handleImageLoad = () => {
		setImageLoadCount(prev => prev + 1)
	}

	// Use live score hook for real-time data (only when card should load)
	const { getCurrentScore, loading: scoreLoading } = useLiveScore({
		matchId: shouldLoad ? match.id : 0, // Only enable when loaded
		id_api: shouldLoad ? match.id_api : undefined,
		match_status: match.match_status,
		home_score: match.home_score,
		away_score: match.away_score,
		autoRefresh: shouldLoad, // Only enable when loaded
		refreshInterval: 30000 // 30 seconds
	})
	
	// Detect when scores are actually ready (immediate for database scores)
	useEffect(() => {
		// If we have database scores, show immediately
		if (match.home_score !== null || match.away_score !== null) {
			setScoresReady(true)
			return
		}
		
		// For API scores, wait for data
		const score = getCurrentScore()
		if (score.home_score !== null || score.away_score !== null || !scoreLoading) {
			setScoresReady(true)
		}
	}, [getCurrentScore, scoreLoading, match.home_score, match.away_score])

	const currentScore = getCurrentScore()
	
	// Get status from new database table wp_sport_matches (1=upcoming, 2=finished, 3=live)
	const dbStatus = String(match.match_status)
	const isLive = dbStatus === '3' || currentScore.is_live
	const isFinished = dbStatus === '2'
	const isUpcoming = dbStatus === '1'

	// Get detailed match status display - prioritize API data for live matches, database status for others
	const statusInfo = (() => {
		// For live matches (status 3), try to get detailed info from API
		if (dbStatus === '3' && match.id_api && (currentScore.status_short || currentScore.elapsed)) {
			return getMatchStatusDisplay(
				currentScore.status_short || '',
				currentScore.status || '',
				currentScore.elapsed
			)
		}
		
		// Fallback to simple database status
		switch (dbStatus) {
			case '3':
				return {
					displayText: 'Trực tiếp',
					isLive: true
				}
			case '2':
				return {
					displayText: 'Đã kết thúc',
					isLive: false
				}
			case '1':
				return {
					displayText: 'Sắp diễn ra',
					isLive: false
				}
			default:
				return {
					displayText: 'Không rõ',
					isLive: false
				}
		}
	})()

	// Check if we have real score data (either from API or database)
	const hasRealScore = () => {
		// No id_api means no API data possible, only show VS
		if (!match.id_api) {
			return false;
		}
		
		// Upcoming matches should always show VS unless they're actually live
		if (isUpcoming && !currentScore.is_live) {
			return false;
		}
		
		// Check if we have actual score data from live API or database
		const hasApiScore = (currentScore.home_score !== null && currentScore.home_score !== undefined) ||
							(currentScore.away_score !== null && currentScore.away_score !== undefined);
		
		const hasDbScore = (match.home_score !== null && match.home_score !== undefined) || 
						  (match.away_score !== null && match.away_score !== undefined);
		
		return hasApiScore || hasDbScore;
	}

	// 🚀 Auto-detect duplicate matches and generate index (WordPress-style với BLV)
	const getDuplicateIndex = () => {
		if (duplicateIndex !== undefined) {
			return duplicateIndex
		}

		if (!allMatches.length) return 0

		// Tìm các matches duplicate (cùng teams, cùng ngày, cùng league) - giống WordPress
		const matchDate = match.time_start ? new Date(match.time_start).toDateString() : ''
		const duplicateMatches = allMatches.filter(m => {
			// Check cùng teams (case insensitive)
			const sameTeams = (m.team_hone?.toLowerCase() === match.team_hone?.toLowerCase()) &&
							 (m.team_away?.toLowerCase() === match.team_away?.toLowerCase())
			
			// Check cùng ngày
			const mDate = m.time_start ? new Date(m.time_start).toDateString() : ''
			const sameDate = mDate === matchDate && matchDate !== ''
			
			// Check cùng league 
			const sameLeague = m.league_name?.toLowerCase() === match.league_name?.toLowerCase()
			
			return sameTeams && sameDate && sameLeague
		})

		// Nếu không có duplicate, return 0 (không có suffix)
		if (duplicateMatches.length <= 1) return 0

		// Sort ALL duplicate matches (bao gồm current match) theo thứ tự WordPress:
		// 1. Theo creation time (older first)
		// 2. Theo BLV name (nếu cùng thời gian)
		// 3. Theo ID (fallback)
		const allDuplicates = [...duplicateMatches]
		allDuplicates.sort((a, b) => {
			// Sort by creation time first
			const timeA = new Date(a.created_at || a.time_start || '').getTime() || 0
			const timeB = new Date(b.created_at || b.time_start || '').getTime() || 0
			
			if (timeA !== timeB) {
				return timeA - timeB
			}
			
			// If same time, sort by caster name (BLV)
			const casterA = (a.caster || '').toLowerCase()
			const casterB = (b.caster || '').toLowerCase()
			
			if (casterA !== casterB) {
				return casterA.localeCompare(casterB)
			}
			
			// Fallback: sort by ID
			return (a.id || 0) - (b.id || 0)
		})

		// Tìm index của current match trong sorted array
		const currentIndex = allDuplicates.findIndex(m => m.id === match.id)
		
		// Return index:
		// - Index 0 (match đầu tiên) = no suffix
		// - Index 1 (match thứ 2) = "-1" 
		// - Index 2 (match thứ 3) = "-2"
		// - etc.
		return currentIndex
	}

	const calculatedDuplicateIndex = getDuplicateIndex()

	// 🚀 WordPress-style URL: tự tạo slug với duplicate index
	const matchUrl = generateWordPressMatchUrl(
		match.team_hone || 'Đội nhà',
		match.team_away || 'Đội khách',
		match.league_name,
		match.time_start,
		calculatedDuplicateIndex > 0 ? calculatedDuplicateIndex : undefined
	)

	const formatTime = (timeStart?: string) => {
		if (!timeStart) return ''
		const d = new Date(timeStart)
		return `${d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} ${d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}`
	}

	const firstCaster = (match.caster || '').split(',').map(s => s.trim()).filter(Boolean)[0]
	const casterAvatarUrl = getCasterAvatarUrlWithFallback(match.caster_avatar)

	// 🚀 WordPress-style: không prefetch, đơn giản

	// Helper function to get final score (prioritize live data over database)
	const getHomeScore = () => {
		return currentScore.home_score !== null && currentScore.home_score !== undefined 
			? currentScore.home_score 
			: (Number(match.home_score) || 0);
	}

	const getAwayScore = () => {
		return currentScore.away_score !== null && currentScore.away_score !== undefined 
			? currentScore.away_score 
			: (Number(match.away_score) || 0);
	}

	// Helper function to ensure score is always a number, never empty
	const formatScore = (score: any) => {
		if (score === null || score === undefined || score === '') {
			return 0;
		}
		const num = parseInt(String(score));
		return isNaN(num) ? 0 : num;
	}

	// Show skeleton when not loaded - matches actual card structure
	if (!shouldLoad) {
		return (
			<div 
				ref={skeletonRef}
				className="fs-match-card loading-skeleton"
				style={{
					display: 'block',
					textDecoration: 'none',
					color: 'inherit',
					pointerEvents: 'none'
				}}
			>
				{/* Match Header - Matches fs-match-header */}
				<div className="fs-match-header">
					<div className="gmd-tournament-header">
						<div className="gmd-match-league">
							<div 
								className="gmd-comp_logo"
								style={{
									width: '14px',
									height: '14px',
									marginRight: '5px',
									backgroundColor: '#666',
									borderRadius: '2px',
									animation: 'pulse 1.5s ease-in-out infinite'
								}}
							></div>
							<span 
								className="text-ellipsis"
								style={{
									backgroundColor: '#666',
									color: 'transparent',
									borderRadius: '3px',
									height: '14px',
									width: '80px',
									display: 'inline-block',
									animation: 'pulse 1.5s ease-in-out infinite'
								}}
							>Loading...</span>
						</div>
					</div>
					
					<div className="gmd-elapsed-time">
						<div className="gmd-elapsed-wrapper icon-haflt">
							<div 
								className="gmd-elapsed"
								style={{
									width: '12px',
									height: '12px',
									backgroundColor: '#666',
									borderRadius: '2px',
									animation: 'pulse 1.5s ease-in-out infinite'
								}}
							></div>
							<p 
								className="gmd-e_minutes grid-match__status--normal t_time time t_time_num"
								style={{
									backgroundColor: '#666',
									color: 'transparent',
									borderRadius: '2px',
									fontSize: '12px',
									fontWeight: '700',
									padding: '2px 8px',
									width: '40px',
									height: '16px',
									animation: 'pulse 1.5s ease-in-out infinite'
								}}
							>Loading</p>
						</div>
					</div>
					
					<div className="fs-match-time">
						<span
							style={{
								backgroundColor: '#666',
								color: 'transparent',
								borderRadius: '3px',
								padding: '2px 8px',
								fontSize: '12px',
								display: 'inline-block',
								width: '60px',
								height: '16px',
								animation: 'pulse 1.5s ease-in-out infinite'
							}}
						>00:00</span>
					</div>
				</div>

				{/* Match Content - Matches fs-match-content */}
				<div className="fs-match-content">
					{/* Home Team */}
					<div className="fs-team home">
						<div
							style={{
								width: '45px',
								height: '45px',
								backgroundColor: '#666',
								borderRadius: '50%',
								animation: 'pulse 1.5s ease-in-out infinite'
							}}
						></div>
						<span 
							className="fs-team-name"
							style={{
								backgroundColor: '#666',
								color: 'transparent',
								borderRadius: '2px',
								width: '60px',
								height: '12px',
								display: 'inline-block',
								animation: 'pulse 1.5s ease-in-out infinite'
							}}
						>Loading</span>
					</div>

					{/* Match Center */}
					<div className="fs-match-center">
						<div className="fs-match-score">
							<div className="grid-match__status gmd-mid_score text-center">
								<div 
									className="fs-score __vs"
									style={{
										backgroundColor: '#666',
										color: 'transparent',
										borderRadius: '4px',
										width: '40px',
										height: '24px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										animation: 'pulse 1.5s ease-in-out infinite'
									}}
								>VS</div>
							</div>
						</div>
					</div>

					{/* Away Team */}
					<div className="fs-team away">
						<div
							style={{
								width: '45px',
								height: '45px',
								backgroundColor: '#666',
								borderRadius: '50%',
								animation: 'pulse 1.5s ease-in-out infinite'
							}}
						></div>
						<span 
							className="fs-team-name"
							style={{
								backgroundColor: '#666',
								color: 'transparent',
								borderRadius: '2px',
								width: '60px',
								height: '12px',
								display: 'inline-block',
								animation: 'pulse 1.5s ease-in-out infinite'
							}}
						>Loading</span>
					</div>
				</div>

				{/* Match Footer */}
				<div className="fs-match-footer">
					<div className="gmd-match-footer__flag">
						<div className="grid-match--flag">
							<div className="gmd-match__goal-scoring d-flex">
								<span 
									className="gmd-ht"
									style={{
										backgroundColor: '#666',
										color: 'transparent',
										borderRadius: '2px',
										fontSize: '11px',
										width: '18px',
										height: '11px',
										display: 'inline-block',
										animation: 'pulse 1.5s ease-in-out infinite'
									}}
								>HT</span>
								<span 
									className="gmd-match__half-court halfCourt"
									style={{
										backgroundColor: '#666',
										color: 'transparent',
										borderRadius: '2px',
										fontSize: '11px',
										width: '30px',
										height: '11px',
										display: 'inline-block',
										marginLeft: '3px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}}
								>0-0</span>
							</div>
							<div className="gmd-match__corner-ball d-flex">
								<div
									style={{
										width: '12px',
										height: '12px',
										backgroundColor: '#666',
										borderRadius: '2px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}}
								></div>
								<span 
									className="gmd-match__corner-goal cornerGoal"
									style={{
										backgroundColor: '#666',
										color: 'transparent',
										borderRadius: '2px',
										fontSize: '11px',
										width: '30px',
										height: '11px',
										display: 'inline-block',
										marginLeft: '3px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}}
								>0-0</span>
							</div>
							<div className="gmd-match__corner-yellow d-flex">
								<div
									style={{
										width: '10px',
										height: '10px',
										backgroundColor: '#666',
										borderRadius: '2px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}}
								></div>
								<span 
									className="gmd-match__home-yellow cornerYellow"
									style={{
										backgroundColor: '#666',
										color: 'transparent',
										borderRadius: '2px',
										fontSize: '11px',
										width: '30px',
										height: '11px',
										display: 'inline-block',
										marginLeft: '3px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}}
								>0-0</span>
							</div>
						</div>
					</div>

					<div className="gmd-match-footer__streamer number-blv-1 max-three-blv">
						<div className="extra-info_one extra-info_list int-blv">
							<div
								style={{
									width: '35px',
									height: '35px',
									backgroundColor: '#666',
									borderRadius: '50%',
									animation: 'pulse 1.5s ease-in-out infinite'
								}}
							></div>
							<span
								style={{
									backgroundColor: '#666',
									color: 'transparent',
									borderRadius: '2px',
									fontSize: '12px',
									fontWeight: '500',
									width: '40px',
									height: '16px',
									display: 'inline-block',
									animation: 'pulse 1.5s ease-in-out infinite'
								}}
							>BLV</span>
						</div>
					</div>

					<div className="gmd-match-footer__odds">
						<div
							style={{
								backgroundColor: '#666',
								borderRadius: '3px',
								width: '50px',
								height: '32px',
								animation: 'pulse 1.5s ease-in-out infinite'
							}}
						></div>
					</div>
				</div>

				<style jsx>{`
					@keyframes pulse {
						0%, 100% {
							opacity: 0.6;
						}
						50% {
							opacity: 0.8;
						}
					}
				`}</style>
			</div>
		)
	}

	return (
		// 🚀 WordPress-style: đơn giản Link wrapper, nhanh như WordPress
		<Link 
			ref={cardRef}
			href={matchUrl}
			className={`fs-match-card${isLive ? ' is-live' : ''}`}
			style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
		>

			{/* Match Header */}
			<div className="fs-match-header">
				<div className="gmd-tournament-header">
					<div className="gmd-match-league">
						{imagesLoaded && match.league_avatar ? (
							<img 
								className="gmd-comp_logo" 
								src={getTeamLogoUrlWithFallback(match.league_avatar).src} 
								alt={match.league_name || 'League'} 
								loading="lazy" 
								decoding="async"
								style={{width: '14px', height: '14px', marginRight: '5px', objectFit: 'contain'}}
								onLoad={(e) => {
									e.currentTarget.classList.add('loaded')
								}}
								onError={(e) => {
									(e.target as HTMLImageElement).src = getTeamLogoUrlWithFallback(match.league_avatar).fallback
								}}
							/>
						) : (
							<div 
								className="gmd-comp_logo bg-gray-600 rounded-sm" 
								style={{width: '14px', height: '14px', marginRight: '5px'}}
							></div>
						)}
						<span className="text-ellipsis">{match.league_name || 'Giải đấu'}</span>
					</div>
				</div>
				
				{(isLive || isUpcoming || isFinished) && (
					<div className="gmd-elapsed-time">
						<div className="gmd-elapsed-wrapper icon-haflt" data-fid="4wyrn4h694e4q86">
							<img 
								src="https://105800054d9.vws.vegacdn.vn/xoilacstatic/images/elapsed.svg" 
								className="gmd-elapsed" 
								alt="Elapsed time"
								loading="lazy"
								onLoad={(e) => {
									e.currentTarget.classList.add('loaded')
								}}
							/>
							<p id="elapsedTime" className="gmd-e_minutes grid-match__status--normal gmd-e_minutes t_time time t_time_num" data-time="">
								{statusInfo.displayText}
							</p>
						</div>
					</div>
				)}
				
				<div className="fs-match-time">
					<span>{formatTime(match.time_start)}</span>
				</div>
			</div>

			{/* Match Content */}
			<div className="fs-match-content">
				{/* Home Team */}
				<div className="fs-team home">
					{imagesLoaded && match.team_hone_avatar ? (
						<img 
							src={getTeamLogoUrlWithFallback(match.team_hone_avatar).src} 
							alt={match.team_hone || 'Đội nhà'} 
							loading="eager" 
							decoding="async"
							fetchPriority="high"
							onLoad={(e) => {
								e.currentTarget.classList.add('loaded')
								handleImageLoad()
							}}
							onError={(e) => {
								(e.target as HTMLImageElement).src = getTeamLogoUrlWithFallback(match.team_hone_avatar).fallback
							}}
						/>
					) : (
						<div 
							className="bg-gray-600 rounded-full" 
							style={{width: '45px', height: '45px'}}
						></div>
					)}
					<span className="fs-team-name">{match.team_hone || 'Đội nhà'}</span>
				</div>

				{/* Match Center - Score */}
				<div className="fs-match-center">
					<div className="fs-match-score">
						<div className="grid-match__status gmd-mid_score text-center" data-fid="dj2ryoh1op7zq1z">
							{!scoresReady || !hasRealScore() ? (
								<div className="fs-score __vs">VS</div>
							) : (
								<div className="fs-score gmd-score-box grid-match__vs t_vs_num">
									<div className={`gmd_home-score ${getHomeScore() > getAwayScore() ? 'is-win' : ''}`}>
										<p>{scoreLoading && !match.id_api ? '-' : getHomeScore()}</p>
									</div>
									<span> : </span>
									<div className={`gmd_away-score ${getAwayScore() > getHomeScore() ? 'is-win' : ''}`}>
										<p>{scoreLoading && !match.id_api ? '-' : getAwayScore()}</p>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Away Team */}
				<div className="fs-team away">
					{imagesLoaded && match.team_away_avatar ? (
						<img 
							src={getTeamLogoUrlWithFallback(match.team_away_avatar).src} 
							alt={match.team_away || 'Đội khách'} 
							loading="eager" 
							decoding="async"
							fetchPriority="high"
							onLoad={(e) => {
								e.currentTarget.classList.add('loaded')
								handleImageLoad()
							}}
							onError={(e) => {
								(e.target as HTMLImageElement).src = getTeamLogoUrlWithFallback(match.team_away_avatar).fallback
							}}
						/>
					) : (
						<div 
							className="bg-gray-600 rounded-full" 
							style={{width: '45px', height: '45px'}}
						></div>
					)}
					<span className="fs-team-name">{match.team_away || 'Đội khách'}</span>
				</div>
			</div>

			{/* Match Footer */}
			<div className="fs-match-footer">
				{/* Stats Flags */}
				<div className="gmd-match-footer__flag">
					<div className="grid-match--flag">
						<div className="gmd-match__goal-scoring d-flex">
							<span className="gmd-ht">HT</span>
							<span className="gmd-match__half-court halfCourt">0 - 0</span>
						</div>
						<div className="gmd-match__corner-ball d-flex">
							<img 
								src="https://105800054d9.vws.vegacdn.vn/xoilacstatic/images/home_capa.svg" 
								alt="Flag" 
								width="12px" 
								height="12px"
								loading="lazy"
								onLoad={(e) => {
									e.currentTarget.classList.add('loaded')
								}}
							/>
							<span className="gmd-match__corner-goal cornerGoal">0 - 0</span>
						</div>
						<div className="gmd-match__corner-yellow d-flex">
							<img 
								src="https://105800054d9.vws.vegacdn.vn/xoilacstatic/images/home_yellow.svg" 
								alt="Flag" 
								width="10px" 
								height="10px"
								loading="lazy"
								onLoad={(e) => {
									e.currentTarget.classList.add('loaded')
								}}
							/>
							<span className="gmd-match__home-yellow cornerYellow">0 - 0</span>
						</div>
					</div>
				</div>

				{/* Caster Info */}
				<div className="gmd-match-footer__streamer number-blv-1 max-three-blv">
					<div className="extra-info_one extra-info_list int-blv">
						<svg width="35" height="35" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
							<path
								d="M15.2328 2.96681L19.5921 6.134C21.5198 7.53454 22.3264 10.0171 21.5901 12.2832L19.925 17.4078C19.1887 19.6739 17.0769 21.2082 14.6942 21.2082H9.30583C6.92309 21.2082 4.81133 19.6739 4.07502 17.4078L2.40993 12.2832C1.67363 10.0171 2.48025 7.53454 4.40793 6.134L8.76718 2.96682C10.6949 1.56627 13.3051 1.56627 15.2328 2.96681Z"
								fill={`url(#pattern_caster_${match.id})`} 
								stroke="#FFC107"
							/>
							<path
								d="M8.76718 2.96682C10.6949 1.56627 13.3051 1.56627 15.2328 2.96681L19.5921 6.134C21.5198 7.53454 22.3264 10.0171 21.5901 12.2832L19.925 17.4078C19.1887 19.6739 17.0769 21.2082 14.6942 21.2082H9.30583C6.92309 21.2082 4.81133 19.6739 4.07502 17.4078L2.40993 12.2832C1.67363 10.0171 2.48025 7.53454 4.40793 6.134L8.76718 2.96682Z"
								stroke="#FFC107"
							/>
							<defs>
								<pattern 
									id={`pattern_caster_${match.id}`}
									patternContentUnits="objectBoundingBox" 
									width="1" 
									height="1"
								>
									<use 
										xlinkHref={`#image_caster_${match.id}`}
										transform="scale(0.000510204)"
									/>
								</pattern>
								<image 
									id={`image_caster_${match.id}`} 
									width="1960" 
									height="1960"
									xlinkHref={imagesLoaded ? casterAvatarUrl.src : ''}
								/>
							</defs>
						</svg>
						<span>{firstCaster || 'BLV Trực tiếp'}</span>
					</div>
				</div>

				{/* Odds/Betting Button */}
				<div className="gmd-match-footer__odds">
					<a 
						href="https://3456789day.com/?f=2843315" 
						className="odds-comp" 
						target="_blank" 
						rel="nofollow"
						style={{position: 'relative', zIndex: 2}}
					>
						<img 
							data-url="#"
							src="https://105800054d9.vws.vegacdn.vn/xoilacstatic/images/okfun_gift.gif" 
							alt="okfun"
							loading="lazy"
							onLoad={(e) => {
								e.currentTarget.classList.add('loaded')
								handleImageLoad()
							}}
						/>
					</a>
				</div>
			</div>
		</Link>
	)
}